import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MenuComponent from "../../components/menu/menu.component";
import Spinner from "../../components/spinner/spinner.component";
import {
  selectKitchenIsLoading,
  selectKitchenError,
  selectDishesMap,
  selectFeaturedPromoDishes,
  selectKitchen,
  selectOnlineOrdersConfigs,
  FEATURED_PROMO_SECTION_ID,
} from "../../store/kitchen/kitchen.selector";
import {
  HeaderContainer,
  HeaderTitle,
  MenuBody,
  HeaderRight,
  StatusContainer,
  BannerContainer,
  HeaderContent,
  HeaderTextSmall,
  HeaderItem,
  MainHeaderContainer,
  MainHeader,
  IconContainer,
  HeaderTopRow,
  HeaderAddressRow,
  AdressBox,
  ImageSkeleton,
  BannerDisplayImage,
} from "./menu.styles";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MenuHeaders from "../../components/menu-headers/menu-headers.component";
import Footer from "../../components/footer/footer.component";
import CartButton from "../../components/cart-button/cart-button.component";
import {
  selectCartKitchenId,
  selectDeliveryAddress,
  selectScheduledTime,
  selectCartCount,
} from "../../store/cart/cart.selector";
import {
  clearCart,
  setDeliveryAddress,
  setOrderNote,
  setScheduledTime,
} from "../../store/cart/cart.action";
import { kitchenClosedAutoSet, kitchenClosedOverride } from "../../constants";
import {
  EmptyCartSubtitleText,
  EmptyCartText,
} from "../checkout/checkout.styles";
import NotFoundImage from "../../assets/not-found.png";
import { StandardContainer } from "../../components/menu/menu.styles";
import { SideDrawer } from "../../components/side-drawer/side-drawer";
import { fetchKitchenAsync } from "../../store/kitchen/kitchen.action";
import { Box } from "@mui/material";
import { analytics } from "../../utils/firebase/firebase.utils";
import { logEvent } from "firebase/analytics";
import OrderTypeBottomSheet from "../../components/order-type-bottom-sheet/order-type-bottom-sheet.component";
import {
  formatScheduledPickupTime,
  getFontName,
  isOnlineOrderConfigValid,
  useMediaQuery,
} from "../../utils/utils.common";
import Status from "../../components/shared/status/status.component";
import OrderTypeButtonGroup from "../../components/shared/buttonGroup/orderTypeButtonGroup.component";
import ButtonAction from "../../components/shared/buttonAction/buttonAction.component";
import { getColor } from "../../utils/utils.common";
import { ClockIcon, HashIcon, MarkerIcon } from "../../components/icons";
import Search from "../../components/shared/search/search.component";
import {
  getEnabledOrderTypes,
  ORDER_TYPE_QUERY_PARAM,
  resolveOrderType,
  TABLE_NUMBER_QUERY_PARAM,
} from "../../utils/orderType";
import {
  businessOpenCheck,
  findNextOpening,
} from "../../utils/closedKitchenCheck";
import { formatDeliveryAddress, isDeliveryAddressSet } from "../../utils/deliveryAddress";
import {
  isDeliveryOrderingAvailable,
} from "../../utils/deliveryConfig";
import { useDebounce } from "../../utils/useDebounce";
import EmptyState from "../../components/shared/emptyState/emptyState.component";

const Menu = () => {
  const { pathname } = useLocation();
  // Get the kitchen name from the url to pass to other compoenent
  const { kitchenSlug } = useParams();
  const searchParams = new URLSearchParams(window.location.search);

  // Get individual query parameters
  const tableNumber = searchParams.get("tableNumber");
  const categoriesMap = useSelector(selectDishesMap);
  const featuredPromoDishes = useSelector(selectFeaturedPromoDishes);

  // console.log("categoriesMap: ")
  const kitchen = useSelector(selectKitchen);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm);

  const [isKitchenOpen, setIsKitchenOpen] = useState(false);
  const allCategories = kitchen?.dishCategories ? kitchen.dishCategories : null;

  const getScheduledTime = useSelector(selectScheduledTime);
  const deliveryAddress = useSelector(selectDeliveryAddress);
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
  const cartCount = useSelector(selectCartCount);

  const getOrderType = resolveOrderType(searchParams, onlineOrdersConfig);
  const isDeliveryAvailable = isDeliveryOrderingAvailable(
    kitchen?.deliverySchedule,
    onlineOrdersConfig?.onlineDeliveryPaused,
    kitchen?.timezone
  );

  const [currentOrderType, setCurrentOrderType] = useState(getOrderType);
  const enabledOrderTypes = getEnabledOrderTypes(onlineOrdersConfig);

  useEffect(() => {
    setCurrentOrderType(getOrderType);
  }, [getOrderType]);

  const textBody = getColor("text-body", onlineOrdersConfig);
  const backgroundTertiary = getColor(
    "background-tertiary",
    onlineOrdersConfig
  );
  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);
  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );

  const fieldBorder = getColor("field-border", onlineOrdersConfig);
  const fieldBackground = getColor("field-background", onlineOrdersConfig);
  const fieldPlaceholderText = getColor(
    "field-placeholder-text",
    onlineOrdersConfig
  );
  const fieldFilledText = getColor("field-filled-text", onlineOrdersConfig);
  const brandFontStyle = getFontName("brand-font-style", onlineOrdersConfig);
  const isDarkHex = (hexColor) => {
    if (!hexColor || typeof hexColor !== "string" || !hexColor.startsWith("#")) {
      return false;
    }
    const normalized =
      hexColor.length === 4
        ? `#${hexColor[1]}${hexColor[1]}${hexColor[2]}${hexColor[2]}${hexColor[3]}${hexColor[3]}`
        : hexColor;
    const r = parseInt(normalized.slice(1, 3), 16);
    const g = parseInt(normalized.slice(3, 5), 16);
    const b = parseInt(normalized.slice(5, 7), 16);
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance < 0.5;
  };
  const lightenHex = (hexColor, amount = 0.06) => {
    if (!hexColor || typeof hexColor !== "string" || !hexColor.startsWith("#")) {
      return hexColor;
    }
    const normalized =
      hexColor.length === 4
        ? `#${hexColor[1]}${hexColor[1]}${hexColor[2]}${hexColor[2]}${hexColor[3]}${hexColor[3]}`
        : hexColor;
    const r = parseInt(normalized.slice(1, 3), 16);
    const g = parseInt(normalized.slice(3, 5), 16);
    const b = parseInt(normalized.slice(5, 7), 16);
    const mix = (value) => Math.min(255, Math.round(value + (255 - value) * amount));
    return `#${mix(r).toString(16).padStart(2, "0")}${mix(g).toString(16).padStart(2, "0")}${mix(b).toString(16).padStart(2, "0")}`;
  };
  const headerSkeletonBase = lightenHex(headerModalBackground, 0.06);
  const bannerSkeletonBase = lightenHex(
    getColor("background-primary", onlineOrdersConfig),
    0.06
  );
  const skeletonHighlight = isDarkHex(headerSkeletonBase)
    ? "rgba(255, 255, 255, 0.18)"
    : "rgba(0, 0, 0, 0.06)";

  const shouldShowCartButton =
    (kitchen?.kitchenOrderStatus === kitchenClosedAutoSet && isKitchenOpen) ||
    (kitchen?.kitchenOrderStatus !== kitchenClosedAutoSet &&
      kitchen?.kitchenOrderStatus !== kitchenClosedOverride);

  const isLoading = useSelector(selectKitchenIsLoading);
  const kitchenLoadError = useSelector(selectKitchenError);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [showOrderTypeBottomSheet, setShowOrderTypeBottomSheet] =
    useState(false);
  const [checkDrawerState, setCheckDrawerState] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [bannerLoaded, setBannerLoaded] = useState(false);

  const cartKitchenId = useSelector(selectCartKitchenId);
  const dispatch = useDispatch();

  const findNextOpeningTime = findNextOpening(
    kitchen?.hours?.schedule ?? null,
    kitchen?.timezone
  );
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchKitchenAsync(pathname));
  }, [dispatch, pathname]);

  useEffect(() => {
    logEvent(analytics, "homepage_visited", {
      kitchenName: kitchen?.kitchenName,
    });
    const isOpen = businessOpenCheck(
      kitchen?.hours?.schedule ?? null,
      kitchen?.timezone
    );
    setIsKitchenOpen(isOpen);
  }, [kitchen]);

  useEffect(() => {
    // Clear Cart if new restaurant is loaded
    if (kitchen?.kitchenId !== cartKitchenId) {
      // const orderType =
      //   onlineOrdersConfig?.takeAwayEnabled && !tableNumber
      //     ? "takeaway"
      //     : "dinein";
      const scheduledTime = null;
      // console.log("========>>");
      // console.log(orderType);
      // console.log("========>>>>" + tableNumber)
      // dispatch(setOrderType(orderType));
      dispatch(setOrderNote(null));
      dispatch(setScheduledTime(scheduledTime));
      dispatch(clearCart());
    }
  }, [dispatch, cartKitchenId, kitchen, onlineOrdersConfig, tableNumber]);

  useEffect(() => {
    if (onlineOrdersConfig) {
      document.body.style.backgroundColor = getColor(
        "header-modal-background",
        onlineOrdersConfig
      );
    }
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, [onlineOrdersConfig]);

  const handlerShowOrderTypeBottomSheet = () => {
    setShowOrderTypeBottomSheet(!showOrderTypeBottomSheet);
  };

  const dishMatchesSearch = (dish, term) =>
    dish.dishName.toLowerCase().includes(term.toLowerCase()) ||
    dish.dishDescription?.toLowerCase().includes(term.toLowerCase());

  const sortCategories = (allDishes, searchTerm = "") => {
    // Create a mapping from category name to category number
    const categoryMap2 = new Map();
    allCategories?.forEach((category) => {
      categoryMap2.set(category.name, category.categoryNumber);
    });

    // Function to get the category number from the map key
    const getCategoryNumber = (category) => {
      return categoryMap2.get(category) || Infinity;
    };

    // Sort the map keys based on the category number
    const sortedMap = new Map(
      [...categoriesMap.entries()].sort((a, b) => {
        return getCategoryNumber(a[0]) - getCategoryNumber(b[0]);
      })
    );

    let content = [];
    let hasAnyMatch = false;

    const visibleFeaturedPromos = searchTerm
      ? featuredPromoDishes.filter((dish) => dishMatchesSearch(dish, searchTerm))
      : featuredPromoDishes;

    if (visibleFeaturedPromos.length > 0) {
      hasAnyMatch = true;
      content.push(
        <MenuComponent
          key={FEATURED_PROMO_SECTION_ID}
          title={FEATURED_PROMO_SECTION_ID}
          products={visibleFeaturedPromos}
          kitchen={kitchen}
          brandFontStyle={brandFontStyle}
        />
      );
    }

    for (const [title, dishes] of sortedMap) {
      const filteredDishes = dishes.filter((dish) =>
        dishMatchesSearch(dish, searchTerm)
      );

      if (searchTerm && filteredDishes.length === 0) continue;

      if (filteredDishes.length > 0) {
        hasAnyMatch = true;
      }

      const customTitle = title?.match(/^\d/) ? title.substring(3) : title;

      content.push(
        <MenuComponent
          key={customTitle}
          title={allDishes.size <= 1 ? undefined : customTitle}
          products={searchTerm ? filteredDishes : dishes}
          kitchen={kitchen}
          brandFontStyle={brandFontStyle}
        />
      );
    }

    if (searchTerm && !hasAnyMatch) {
      return (
        <EmptyState
          largeText="No matching items found."
          normalText="Clear the search field or try a different term."
        />
      );
    }

    return content;
  };

  if (isLoading) {
    return (
      <div
        style={{
          position: "absolute",
          left: window.innerWidth / 2 - 35,
          top: window.innerHeight / 2 - 35,
        }}
      >
        <Spinner color={brandPrimary} />
      </div>
    );
  }

  /* Before fetchKitchenStart runs (or rehydrate gap): avoid flashing main menu / "Visual menu only". */
  if (!isLoading && !kitchen?.kitchenId && !kitchenLoadError) {
    return (
      <div
        style={{
          position: "absolute",
          left: window.innerWidth / 2 - 35,
          top: window.innerHeight / 2 - 35,
        }}
      >
        <Spinner color={brandPrimary} />
      </div>
    );
  }

  if (!isLoading && kitchenLoadError) {
    const errorMessage =
      typeof kitchenLoadError?.message === "string" &&
      kitchenLoadError.message.trim()
        ? kitchenLoadError.message
        : "Please check your connection and try again.";
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            py: 3,
            maxWidth: 526,
            width: "100%",
            alignSelf: "center",
          }}
        >
          <img
            src={NotFoundImage}
            style={{ paddingBottom: "44px", maxWidth: "100%" }}
            alt=""
          />
          <EmptyCartText>Unable to load this restaurant</EmptyCartText>
          <EmptyCartSubtitleText>{errorMessage}</EmptyCartSubtitleText>
        </Box>
        <Box sx={{ width: "100%", flexShrink: 0, mt: "auto" }}>
          <Footer />
        </Box>
      </Box>
    );
  }

  if (kitchen === null && !isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            py: 3,
            maxWidth: 526,
            width: "100%",
            alignSelf: "center",
          }}
        >
          <img
            src={NotFoundImage}
            style={{ paddingBottom: "44px", maxWidth: "100%" }}
            alt="page-not-found"
          />
          <EmptyCartText>Page not found</EmptyCartText>
          <EmptyCartSubtitleText>
            The page you are looking for doesn’t exist. Check the link and try
            again.
          </EmptyCartSubtitleText>
        </Box>
        <Box sx={{ width: "100%", flexShrink: 0, mt: "auto" }}>
          <Footer />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <MainHeaderContainer
          headerModalBackground={headerModalBackground}
          backgroundTertiary={backgroundTertiary}
          checkDrawerState={checkDrawerState}
        >
          <MainHeader>
            <SideDrawer
              kitchen={kitchen}
              setCheckDrawerState={setCheckDrawerState}
            />
            {onlineOrdersConfig?.logoURL ? (
              <div
                style={{
                  position: "relative",
                  height: "52px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  visibility: !(openSearch && isMobile) ? "visible" : "hidden",
                }}
              >
                <ImageSkeleton
                  hidden={logoLoaded}
                  rounded={true}
                  skeletonBase={headerSkeletonBase}
                  skeletonHighlight={skeletonHighlight}
                />
                <img
                  src={onlineOrdersConfig.logoURL}
                  style={{
                    height: "52px",
                    display: "block",
                    objectFit: "contain",
                  }}
                  alt=""
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  onLoad={() => setLogoLoaded(true)}
                  onError={() => setLogoLoaded(true)}
                />
              </div>
            ) : (
              <HeaderTitle
                textBody={textBody}
                brandPrimary={brandPrimary}
                brandFontStyle={brandFontStyle}
                visible={!(openSearch && isMobile)}
              >
                {kitchen?.kitchenName}
              </HeaderTitle>
            )}
            <Search
              openSearch={openSearch}
              setOpenSearch={setOpenSearch}
              setSearchTerm={setSearchTerm}
              fieldBorder={fieldBorder}
              fieldBackground={fieldBackground}
              textBody={fieldFilledText}
              fieldPlaceholderText={fieldPlaceholderText}
              brandTertiary={brandTertiary}
              brandPrimary={brandPrimary}
            />
          </MainHeader>
        </MainHeaderContainer>
        {(!searchTerm || !isMobile) && (
          <>
            <BannerContainer
              imageUrl={onlineOrdersConfig?.bannerImageURL}
              checkDrawerState={checkDrawerState}
            >
              {onlineOrdersConfig?.bannerImageURL && (
                <>
                  <ImageSkeleton
                    hidden={bannerLoaded}
                    skeletonBase={bannerSkeletonBase}
                    skeletonHighlight={skeletonHighlight}
                  />
                  <BannerDisplayImage
                    src={onlineOrdersConfig.bannerImageURL}
                    alt=""
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    onLoad={() => setBannerLoaded(true)}
                    onError={() => setBannerLoaded(true)}
                  />
                </>
              )}
              {/* {isOnlineOrderConfigValid(onlineOrdersConfig) &&
            (isKitchenOpen ? (
              <>
                {isOnlineOrderConfigValid(onlineOrdersConfig) && (
                  <StatusContainer>
                    <Status
                      status="open"
                      primaryText="Open (Accepting Orders)"
                      secondaryText={`Closes ${getOpeningOrClosing}`}
                      hasBanner={!!onlineOrdersConfig?.bannerImageURL}
                    />
                  </StatusContainer>
                )}
              </>
            ) : (
              <StatusContainer>
                <Status
                  status="close"
                  primaryText="Closed"
                  secondaryText={`Opens Tomorrow ${getOpeningOrClosing}`}
                  hasBanner={!!onlineOrdersConfig?.bannerImageURL}
                />
              </StatusContainer>
            ))} */}
              {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                onlineOrdersConfig?.onlineOrderingPaused &&
                isKitchenOpen && (
                  <StatusContainer>
                    <Status
                      status="notCurrently"
                      primaryText="Currently not accepting online orders."
                      hasBanner={!!onlineOrdersConfig?.bannerImageURL}
                    />
                  </StatusContainer>
                )}

              {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                !isKitchenOpen && (
                  <StatusContainer>
                    <Status
                      status="close"
                      primaryText="Closed"
                      secondaryText={`Opens ${findNextOpeningTime?.day} at ${findNextOpeningTime?.time}`}
                      hasBanner={!!onlineOrdersConfig?.bannerImageURL}
                    />
                  </StatusContainer>
                )}
              {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                onlineOrdersConfig?.onlineOrderingPaused === false &&
                isKitchenOpen && (
                  <>
                    {isOnlineOrderConfigValid(onlineOrdersConfig) && (
                      <StatusContainer>
                        <Status
                          status="open"
                          primaryText="Open (Accepting Orders)"
                          secondaryText={`Closes ${findNextOpeningTime?.closingTime}`}
                          hasBanner={!!onlineOrdersConfig?.bannerImageURL}
                        />
                      </StatusContainer>
                    )}
                  </>
                )}
              {!isOnlineOrderConfigValid(onlineOrdersConfig) && (
                <StatusContainer>
                  <Status
                    status="visual"
                    primaryText="Visual Menu Only"
                    secondaryText="Order From Counter"
                    statusCircle={false}
                    hasBanner={!!onlineOrdersConfig?.bannerImageURL}
                  />
                </StatusContainer>
              )}
            </BannerContainer>

            <HeaderContainer headerModalBackground={headerModalBackground}>
              <StandardContainer>
                <HeaderContent>
                  <HeaderTopRow>
                    {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                      enabledOrderTypes.length > 1 &&
                      isKitchenOpen && (
                        <Box
                          sx={{
                            "@media (min-width:769px)": {
                              marginRight: "16px",
                              flexBasis: enabledOrderTypes.length > 2 ? "360px" : "280px",
                              minWidth: enabledOrderTypes.length > 2 ? "300px" : "230px",
                            },
                            "@media (max-width: 768px)": {
                              display: "flex",
                              width: "100%",
                            },
                          }}
                        >
                          <OrderTypeButtonGroup
                            orderTypes={enabledOrderTypes.map((value) => ({
                              value,
                              label:
                                value === "takeaway"
                                  ? "Take Away"
                                  : value === "dinein"
                                  ? "Dine In"
                                  : "Delivery",
                            }))}
                            currentOrderType={currentOrderType}
                            onOrderTypeChange={(orderType) => {
                              if (orderType === "delivery") {
                                setCurrentOrderType("delivery");
                                setShowOrderTypeBottomSheet(true);
                                return;
                              }

                              const params = new URLSearchParams(
                                window.location.search
                              );
                              if (orderType === "dinein") {
                                params.delete(ORDER_TYPE_QUERY_PARAM);
                                params.set(TABLE_NUMBER_QUERY_PARAM, "");
                                dispatch(setDeliveryAddress(null));
                              } else {
                                params.delete(ORDER_TYPE_QUERY_PARAM);
                                params.delete(TABLE_NUMBER_QUERY_PARAM);
                                dispatch(setDeliveryAddress(null));
                              }
                              navigate(`${pathname}?${params.toString()}`);

                              setCurrentOrderType(orderType);
                            }}
                          />
                        </Box>
                      )}
                    <HeaderRight>
                      {isMobile &&
                        currentOrderType !== "delivery" &&
                        kitchen?.fullAddress && (
                        <HeaderAddressRow>
                          <IconContainer textBody={textBody}>
                            <MarkerIcon />
                          </IconContainer>
                          <AdressBox textBody={textBody}>
                            {kitchen.fullAddress}
                          </AdressBox>
                        </HeaderAddressRow>
                      )}
                      {isOnlineOrderConfigValid(onlineOrdersConfig) &&
                        (isKitchenOpen &&
                        onlineOrdersConfig?.onlineOrderingPaused === false ? (
                          <>
                            {isOnlineOrderConfigValid(onlineOrdersConfig) && (
                              <HeaderItem>
                                {currentOrderType === "dinein" ? (
                                  <>
                                    <IconContainer textBody={textBody}>
                                      <HashIcon />
                                    </IconContainer>
                                    <HeaderTextSmall
                                      textBody={textBody}
                                      onClick={handlerShowOrderTypeBottomSheet}
                                    >
                                      {tableNumber !== null
                                        ? `Table ${tableNumber}`
                                        : "Enter Table Number"}
                                    </HeaderTextSmall>
                                  </>
                                ) : currentOrderType === "delivery" ? (
                                  <>
                                    <IconContainer textBody={textBody}>
                                      <MarkerIcon />
                                    </IconContainer>
                                    <HeaderTextSmall
                                      textBody={textBody}
                                      onClick={handlerShowOrderTypeBottomSheet}
                                    >
                                      {!isDeliveryAvailable
                                        ? onlineOrdersConfig?.onlineDeliveryPaused &&
                                          businessOpenCheck(
                                            kitchen?.deliverySchedule,
                                            kitchen?.timezone
                                          )
                                          ? "Delivery paused"
                                          : "Delivery unavailable"
                                        : isDeliveryAddressSet(deliveryAddress)
                                        ? `${
                                            formatScheduledPickupTime(
                                              getScheduledTime
                                            ) === null
                                              ? "ASAP"
                                              : formatScheduledPickupTime(
                                                  getScheduledTime
                                                )
                                          } • ${formatDeliveryAddress(
                                            deliveryAddress
                                          )}`
                                        : "Enter delivery address"}
                                    </HeaderTextSmall>
                                  </>
                                ) : (
                                  <>
                                    <IconContainer textBody={textBody}>
                                      <ClockIcon />
                                    </IconContainer>
                                    <HeaderTextSmall
                                      textBody={textBody}
                                      onClick={handlerShowOrderTypeBottomSheet}
                                    >
                                      {formatScheduledPickupTime(getScheduledTime) ===
                                      null
                                        ? `Pick up ASAP (${onlineOrdersConfig?.orderReadyTime} Mins)`
                                        : `Pick Up ${formatScheduledPickupTime(
                                            getScheduledTime
                                          )}`}
                                    </HeaderTextSmall>
                                  </>
                                )}
                                <div style={{ marginLeft: "12px", flexShrink: 0 }}>
                                  <ButtonAction
                                    label="Change"
                                    onClick={handlerShowOrderTypeBottomSheet}
                                    backgroundTertiary={backgroundTertiary}
                                    brandPrimary={brandPrimary}
                                  />
                                </div>
                              </HeaderItem>
                            )}
                          </>
                        ) : null)}
                    </HeaderRight>
                  </HeaderTopRow>
                </HeaderContent>
              </StandardContainer>
            </HeaderContainer>
          </>
        )}

        {!searchTerm && (
          <MenuHeaders
            categoriesMap={categoriesMap}
            featuredPromoCount={featuredPromoDishes.length}
            allCategories={allCategories}
            headerModalBackground={headerModalBackground}
            textBody={textBody}
            brandPrimary={brandPrimary}
            backgroundTertiary={backgroundTertiary}
          />
        )}

        <MenuBody
          backgroundPrimary={getColor("background-primary", onlineOrdersConfig)}
          searchTerm={searchTerm}
          isMobile={isMobile}
        >
          <StandardContainer>
            {sortCategories(categoriesMap, debouncedSearchTerm)}
          </StandardContainer>
        </MenuBody>
      </Box>
      <div
        style={{
          paddingBottom:
            isOnlineOrderConfigValid(onlineOrdersConfig) &&
            shouldShowCartButton &&
            cartCount > 0
              ? "70px"
              : "0px",
        }}
      >
        <Footer />
      </div>

      {isOnlineOrderConfigValid(onlineOrdersConfig) && (
        <div>
          {shouldShowCartButton && cartCount > 0 && (
            <CartButton
              kitchenSlug={kitchenSlug}
              backgroundTertiary={backgroundTertiary}
              headerModalBackground={headerModalBackground}
              brandPrimary={brandPrimary}
              primaryButtonText={getColor(
                "primary-button-text",
                onlineOrdersConfig
              )}
            />
          )}
        </div>
      )}
      <OrderTypeBottomSheet
        isOpen={showOrderTypeBottomSheet}
        toggleBottomSheet={() => {
          setShowOrderTypeBottomSheet(false);
          setCurrentOrderType(
            resolveOrderType(
              new URLSearchParams(window.location.search),
              onlineOrdersConfig
            )
          );
        }}
        onlineOrdersConfig={onlineOrdersConfig}
        tableNumber={tableNumber}
        orderType={currentOrderType}
      />
    </Box>
  );
};

export default Menu;
